import { Component, OnInit, OnDestroy, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Observable, of, fromEvent, merge, interval, forkJoin, combineLatest, Subscription, animationFrame } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { Store, Actions, ofActionDispatched, Select, ofActionCompleted } from '@ngxs/store';
import { map, filter, take, takeUntil, tap, skipUntil, finalize, pairwise, distinctUntilChanged, startWith } from 'rxjs/operators';
import { WidgetUrlConfig } from 'src/app/widget-config/initial-config';
import { SetupWidget, AuthWidgetFailure, AuthWidget, LogWidgetInteraction } from 'src/app/widget-auth/state/widget.actions';
import { WidgetInteractionEnum } from 'src/app/widget-auth/models/widget-interaction.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { WidgetAPIHandler } from 'src/app/widget-api/api-handler';
import { ViewState } from 'src/app/view/state/view.state';
import { ViewActions } from 'src/app/view/state/view.actions';
import { WidgetWiring, UIConfig } from 'src/app/shared/widget-wiring';
import { LoadStoryFail, SetIncomingStory, LoadStoryChunkSuccess, HumanReadySDK, HumanReadyWeb } from 'src/app/story/state/story.actions';
import { EngineService } from 'src/app/engine/engine.service';
import { WidgetState } from 'src/app/widget-auth/state/widget.state';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { SetChapters, SetAllChaptersBackground } from 'src/app/story/state/chapters/story.chapters.actions';
import { CameraService } from 'src/app/engine/camera.service';
import { SetCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import * as Hammer from 'hammerjs';
import { get, isNil } from 'lodash';
import { SetScrollMode } from 'src/app/engine/state/engine.actions';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { ViewWindow } from 'src/app/view/models/view-window.enum';
import { NodeActions } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { StorageService, LocalStorageEnum } from 'src/app/shared/storage.service';
import { EnvironmentPlatform } from 'src/environments/models/environment-platform';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { environment } from 'src/environments/environment';
import { SetLabelScale } from 'src/app/story/state/labels/story.labels.actions';
import { EnvironmentDeploy } from 'src/environments/models/environment-deploy';
import { FatalErrorCategoryEnum } from 'src/app/shared/error/error.model';
import { WidgetFatalErrorCategoryEnum } from '../../models/widget-error-type.enum';
import { ErrorService } from 'src/app/shared/error.service';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { ChapterUIModel } from 'src/app/story/models/chapter-ui.model';
import { ViewService } from 'src/app/view/view.service';
import { StoryState } from 'src/app/story/state/story.state';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { ViewQuizModeEnum } from 'src/app/view/models/view-quiz-mode.enum';
import { QuizService } from 'src/app/view/components/quiz/view-quiz.service';

declare const BD_DOCUMENT_REFERRER: string;

@Component({
  selector: 'app-widget-master',
  templateUrl: './widget-master.component.html',
  styleUrls: ['./widget-master.component.scss']
})
export class WidgetMasterComponent implements OnInit, OnDestroy {
  title = 'Biodigital Human';

  environmentApplication = EnvironmentApplication;
  environmentPlatform = EnvironmentPlatform;

  // interaction observable
  private interactionReady$: Observable<any>;
  private interactionChance$: Observable<any>;
  private firstInteraction$: Observable<any>;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private actions: Actions,
    private activatedRoute: ActivatedRoute,
    private widgetAPIHandler: WidgetAPIHandler,
    private errorService: ErrorService,
    private engineService: EngineService,
    private cameraService: CameraService,
    private storageService: StorageService,
    private renderer: Renderer2,
    private viewService: ViewService,
    private quizService: QuizService
  ) {
  }

  ngOnInit() {
    // begin error tracking
    this.initErrorHandling();

    // begin interaction tracking
    this.initInteractions();

    const widgetUrlConfig: WidgetUrlConfig = this.activatedRoute.snapshot.data.widgetUrlConfig;
    this.initWidgetBreakpoints(widgetUrlConfig);
    this.initWidgetLabelScale(widgetUrlConfig);
    this.initWidgetLayout(widgetUrlConfig);
    this.initWidgetSetup(widgetUrlConfig);

    this.initWidgetAuth();
    this.initViewAnalytics();
    this.initInteractionAnalytics();

    // init model default states
    this.initBackgroundFromWidget(widgetUrlConfig);
    this.initBackgroundFromChapter();
    this.initCamera(widgetUrlConfig);
    this.initRotation(widgetUrlConfig);
    this.initQuizInteractions(widgetUrlConfig);

    // start listening for API requests
    const enableAPIAnalytics = environment.deploy === EnvironmentDeploy.Production;
    this.widgetAPIHandler.listen(enableAPIAnalytics);

    this.initChaptersReadyListener();
    this.initHumanReadyListener();

    // Dynamic layout
    this.initDynamicLayout();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // setup widget config
  initWidgetSetup(widgetConfig: WidgetUrlConfig) {
    // IE11 resets the document referrer on navigation, resulting in an authentication failure.
    // We capture the referrer on page load in a global variable as a workaround. 
    // This code can be removed once we drop support of IE11.
    const embedded = window !== window.top;
    const url = embedded ? BD_DOCUMENT_REFERRER : window.location.href;


    this.store.dispatch(new SetupWidget(url, embedded, widgetConfig));

    // get the current layout
    const layout = this.store.selectSnapshot(ViewState.getUI);

    // upgrade params to reflect new widget semantics
    const commonConfig = WidgetWiring.convertUrlUIConfigToUIConfig(widgetConfig);

    // create a new layout
    const newLayout = WidgetWiring.createLayoutFromConfig(layout, commonConfig);

    // have our new layout take effect
    this.store.dispatch(new ViewActions.SetUI(newLayout));

    // set default layout after config
    this.store.dispatch(new ViewActions.SetInitialUI(newLayout));

    // handle widget scroll settings
    const scrollMode = WidgetWiring.getScrollSetting(widgetConfig);
    this.store.dispatch(new SetScrollMode(scrollMode));

    // handle widget print setings
    const usePrintImage = WidgetWiring.getUsePrintImage(widgetConfig);

    // handle on demand loading
    const onDemand = WidgetWiring.getOnDemand(widgetConfig);

    // handle widget loading experience
    const { interactType, isInteractActive } = WidgetWiring.getInteractType(
      widgetConfig,
      this.store.selectSnapshot(ViewState.shouldWindowOpen(ViewWindow.Interact)),
      this.store.selectSnapshot(ViewState.getInteractType)
    );

    // view tutorial
    const tutorialViewedCookie = this.storageService.getLocalItem(LocalStorageEnum.GettingStartedTutorial);
    const isTutorialActive = commonConfig.tutorial === true && tutorialViewedCookie !== `${true}`;

    this.store.dispatch([
      new ViewActions.SetOnDemand(onDemand),
      new ViewActions.ToggleWindow(ViewWindow.OnDemand, onDemand),
      new ViewActions.SetInteractType(interactType),
      new ViewActions.ToggleWindow(ViewWindow.Interact, isInteractActive),
      new ViewActions.ToggleWindow(ViewWindow.Tutorial, isTutorialActive),
      new ViewActions.StartTutorial(isTutorialActive),
      new ViewActions.SetUsePrintImage(usePrintImage),
    ]);    
  }

  // auth user widget: embedded only
  initWidgetAuth() {
    const embedded = this.store.selectSnapshot(WidgetState.getEmbedded);
    if (embedded) {
      this.store.dispatch(new AuthWidget());
    }
  }

  initViewAnalytics() {
    // init: wait for document ready	
    const loaded = ['loaded', 'interactive', 'complete'].includes(document.readyState);
    const documentReady$: Observable<any> = loaded ? of(loaded) : fromEvent(document, 'DOMContentLoaded');

    documentReady$
      .pipe(
        take(1), // only initial	
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.View));
      });
  }

  // analytic: first user interaction	
  initInteractionAnalytics() {
    // track interaction chance: explicit chance or forced chance (i.e., interacted w/ scene)
    merge(
      this.interactionChance$,
      this.firstInteraction$
    )
      .pipe(
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.ChanceInteraction));
      });

    // track first interaction
    this.firstInteraction$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.FirstInteraction));
      });
  }

  // error handling
  initErrorHandling() {
    // content errors
    this.actions
      .pipe(ofActionDispatched(LoadStoryFail), takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.handleContentErrors());

    // auth errors
    this.actions
      .pipe(
        ofActionDispatched(AuthWidgetFailure),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((e: AuthWidgetFailure) => this.handleAuthErrors(e));
  }

  // sets custom breakpoints
  initWidgetBreakpoints({ 'ui-collapse-label': collapseLabel, 'annotation-collapse': annotationCollapse }: WidgetUrlConfig) {
    // get label breakpoint
    const labelBreakpoint = [collapseLabel, annotationCollapse].find(value => !isNil(value));

    // validate: 0 allowed
    if (!isNil(labelBreakpoint)) {
      this.store.dispatch(new ViewActions.SetLabelBreakpoint(labelBreakpoint));
    }
  }

  initWidgetLabelScale({'label-scale': labelScale }: WidgetUrlConfig) {
    if (!isNil(labelScale)) {
      this.store.dispatch(new SetLabelScale(labelScale));
    }
  }

  initWidgetLayout({'ui-layout': layout}: WidgetUrlConfig) {
    if (!isNil(layout)) {
      this.store.dispatch(new ViewActions.SetLayout(layout));
    }
  }

  initQuizInteractions(widgetConfig: WidgetUrlConfig) {
    // set quiz state when questions initialized
    this.store.select(QuizState.hasQuiz)
      .pipe(
        filter((hasQuiz) => hasQuiz),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const allQuestions = this.store.selectSnapshot(QuizState.getAllQuestions);

        // set enabled
        const uiQuiz = widgetConfig['uiQuiz'];
        const isQuizEnabled = `${uiQuiz}` !== 'false';
        this.store.dispatch(new QuizActions.SetQuizEnabled(isQuizEnabled));

        // set locked
        const uiQuizLocked = widgetConfig['ui-quiz-locked'];
        const isQuizLocked = `${uiQuizLocked}` === 'true';
        this.store.dispatch(new QuizActions.SetQuizLocked(isQuizLocked));

        // set question range: optional        
        const uiQuizStart = parseInt(widgetConfig['ui-quiz-start'] as any, 10);
        const uiQuizEnd = parseInt(widgetConfig['ui-quiz-end'] as any, 10);
        const questionCount = allQuestions.length;
        const startQuestionNum = !isNaN(uiQuizStart) ? uiQuizStart : 1;
        const endQuestionNum = !isNaN(uiQuizEnd) ? uiQuizEnd : questionCount;

        const ignoreQuestionRange = startQuestionNum === 1 && endQuestionNum === questionCount;
        if (!ignoreQuestionRange) {
          const quizQuestions = allQuestions
            .filter((question, index) => {
              const questionNum = index + 1;
              return questionNum >= startQuestionNum && questionNum <= endQuestionNum;
            });
          this.store.dispatch(new QuizActions.SetQuestions(quizQuestions));
        }

        // set questions mode
        const quizQuestionsMode = !isNaN(uiQuizStart) || !isNaN(uiQuizEnd)
          ? 'restricted'
          : 'all';
        this.store.dispatch(new QuizActions.SetQuizQuestionsMode(quizQuestionsMode));

        // start quiz
        if (uiQuizLocked) {
          setTimeout(() => {
            this.quizService.startQuiz();
          });
        }
      });
  }

  initInteractions() {
    // canvas interaction
    const canvasInteractions$ = this.getCanvasInteraction();

    // app interactions
    const appInteractions$ = this.actions.pipe(
      ofActionDispatched(
        NodeActions.UpdateNodeSelected,
        NodeActions.UpdateNodesChecked,
        NodeActions.UpdateNodesShown,
        NodeActions.UpdateNodesPickable,
        NodeActions.UpdateNodesPaint
      )
    );
    
    // observe: interaction ready
    this.interactionReady$ = this.store.select(StoryChaptersState.getSelectedChapter)
      .pipe(
        filter((chapter) => chapter && chapter.readyState.model),
        take(1)
      );

    // observe: interaction chance, engine scrolled into view
    const interactionElement = this.engineService.getCanvas();
    const intersectionThreshold = 0.75;

    this.interactionChance$ = forkJoin(
      // wait for interaction ready
      this.interactionReady$,
      // wait for scolled into view
      this.getIntersectionObserver(interactionElement, intersectionThreshold)
    )
      .pipe(
        take(1)
      );

    // observe: first interaction
    this.firstInteraction$ = merge(
      canvasInteractions$,
      appInteractions$
    )
      .pipe(
        skipUntil(this.interactionReady$),
        take(1)
      );
  }

  // set initial background
  initBackgroundFromWidget({ background }: WidgetUrlConfig) {
    if (background) {
      this.setWidgetTransparent(BackgroundModel.isTransparent(background));

      // chapters set: patch all backgrounds
      this.actions
        .pipe(
          ofActionCompleted(SetChapters),
          take(1),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(() => {
          this.store.dispatch(new SetAllChaptersBackground(background));
        });
    }
  }

  initBackgroundFromChapter() {
    this.store.select(StoryChaptersState.getSelectedChapter)
      .pipe(
        filter(chapter => chapter != null),
        map(chapter => BackgroundModel.isTransparent(chapter.background)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(isTransparent => {
        this.setWidgetTransparent(isTransparent);
      });
  }

  // set initial camera
  initCamera({ camera }: WidgetUrlConfig) {
    if (camera) {
      // first chunk loaded: set static camera
      this.actions
        .pipe(
          ofActionDispatched(LoadStoryChunkSuccess),
          take(1),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(() => {
          this.store.dispatch(new SetCamera(camera, false));
        });
    }
  }

  // init camera Y rotation
  initRotation({ 'load-rotate': degrees }: WidgetUrlConfig) {
    if (degrees) {

      // animate camera rotation
      let timeLast = performance.now();

      interval(0, animationFrame)
        .pipe(
          map(() => degrees * (performance.now() - timeLast) * 0.001),
          tap(() => timeLast = performance.now()),
          // end animation on interaction
          takeUntil(this.firstInteraction$),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe((deltaY) => {
          this.cameraService.rotateCameraStatic(0, deltaY);
        });

      // return to original camera
      this.firstInteraction$
        .pipe(
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(() => {
          const chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
          const chapterCamera = get(chapter, 'camera.location');

          if (chapterCamera) {
            this.store.dispatch(new SetCamera(chapterCamera, true, 2));
          }
        });
    }
  }


  /**
   * The SDK requires human.ready to fire sooner, so we can deliver events like loading progress.
   * Web clients want human.ready to stay the same.
   */
  initHumanReadyListener() {
    const observable = this.actions.pipe(
      ofActionDispatched(
        environment.platform === EnvironmentPlatform.SDK
          ? HumanReadySDK
          : HumanReadyWeb
      )
    );

    observable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.engineService.fireEvent(EventsEnum.Started, null);
    });
  }

  initChaptersReadyListener() {
    this.store
      .select(StoryChaptersState.getAllChaptersModelsReady)
      .pipe(
        filter(event => event === true),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {

        this.store.dispatch(new HumanReadyWeb());

        this.engineService.fireEvent(EventsEnum.TimelineChaptersActivated, {
          oldChapterIndex: 0,
          newChapterIndex: 0,
          time: 0,
        });
      });
  }


  initDynamicLayout() {
    combineLatest([
      this.store.select(WidgetState.getWidgetApiUIConfig),
      this.store.select(WidgetState.getWidgetUrlConfig),
      this.store.select(StoryChaptersState.getSelectedChapter),
      this.store.select(QuizState.getQuizModeActive),
      this.store.select(QuizState.getActiveQuestion)
    ]).pipe(
      filter(([apiConfig, urlConfig, chapter]) => {
        return chapter != null && urlConfig != null;
      }),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(([apiUIConfig, urlConfig, chapter, quizModeActive, quizQuestion]) => {

      const urlUIConfig = WidgetWiring.convertUrlUIConfigToUIConfig(urlConfig);
      const chapterUIConfig = WidgetWiring.chapterUItoUIConfig(chapter.ui);
      const quizUIConfig = WidgetWiring.generateQuizUIConfig();

      this.viewService.setUI({
        apiUIConfig: apiUIConfig,
        urlUIConfig: urlUIConfig,
        chapterUIConfig: chapterUIConfig,
        quizUIConfig: quizModeActive ? quizUIConfig : null
      });
    });
  }

  handleContentErrors() {
    this.store.dispatch(new SetIncomingStory(null, null));
    this.errorService.redirectToErrorPage(FatalErrorCategoryEnum.Content);
  }
  
  handleAuthErrors(event: AuthWidgetFailure) {
    // Disable authentication redirect on localhost, so that we can test embedded viewers
    if (environment.deploy !== EnvironmentDeploy.Local) {
      this.errorService.redirectToErrorPage(WidgetFatalErrorCategoryEnum.Auth, event.response.errorCode);
    }
  }


  getCanvasInteraction(): Observable<any> {
    let inputManager: HammerManager;

    return new Observable((observer) => {

      // manage canvas interactions
      const canvas = this.engineService.getCanvas();

      inputManager = new Hammer.Manager(canvas);
      inputManager.add([
        new Hammer.Tap({ event: 'singletap' }),
        new Hammer.Tap({ event: 'doubletap', taps: 2 }),
        // handle mouse down/touch down
        new Hammer.Pan({ event: 'pan', direction: Hammer.DIRECTION_ALL, threshold: 0 })
      ]);

      // !important: capture 'this' context
      const handleCanvasInteraction = () => observer.next();
      const handleHammerEvent = handleCanvasInteraction.bind(this);

      // trigger user interaction
      inputManager.on('panstart', handleHammerEvent);
      inputManager.on('singletap', handleHammerEvent);

    }).pipe(
      // cleanup hammer event handling
      finalize(() => {
        if (inputManager) {
          inputManager.destroy();
          inputManager = null;
        }
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  getIntersectionObserver(targetEl: HTMLElement, intersectionThreshold: number): Observable<HTMLElement> {
    return new Observable((observer) => {

      // intersection config
      const intersectionCconfig: IntersectionObserverInit = {
        root: null,
        threshold: intersectionThreshold
      };

      // create intersection observer
      const intersectionObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        // emit observed + cleanup
        const isIntersecting = entries[0].intersectionRatio >= intersectionThreshold;

        if (isIntersecting) {
          // emit observer + cleanup
          observer.next(targetEl);
          observer.complete();

          intersectionObserver.unobserve(targetEl);
        }

      }, intersectionCconfig);

      // begin intersection observing
      intersectionObserver.observe(targetEl);
    });
  }

  setWidgetTransparent(isTransparent) {
    (isTransparent) ?
      this.renderer.setStyle(document.body, 'background', 'none transparent') :
      this.renderer.removeStyle(document.body, 'background');
  }
}
